import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Meteor } from 'meteor/meteor';
import { defer } from '/imports/utils';
import { css } from '/imports/ui/css';
import { Icon, IconKey, IconProps } from '/imports/ui/Icon';
import { Logo } from '/imports/ui/Logo';
import { strings } from '/imports/strings';
import { useUser } from '/imports/hooks';
import { MessagesNavItem } from '/imports/ui/MessagesNavItem';
import { Account, Roles } from '/imports/api/accounts';
import { setSetting } from '/imports/api/users';
import { projectManagementCanSeeFinances } from '/imports/api/projectManagement';

const padding = 1;

const NavStyled = styled.div`
  background: ${css.navBackground};
  padding: ${padding}rem;
  border-right: 1px solid ${css.navBorder};
  display: flex;
  flex-direction: column;
  max-width: 16rem;
  transition: max-width 0.5s ease;
  &.collapsed {
    max-width: 3.5rem;
    .logo img {
      width: 0 !important;
    }
  }
  .logo-wrapper {
    width: 14rem;
    position: relative;
    display: flex;
    justify-content: center;
    margin-bottom: 2rem;
    transition: width 0.5s ease;
    &.collapsed {
      width: 2.5rem;
    }
    .icon {
      cursor: pointer;
      transition: transform 0.5s ease;
      &.collapsed {
        transform: scale(-1, -1);
      }
      svg * {
        stroke: ${css.navText};
        fill: ${css.navText};
      }
    }
    .logo {
      /* display: block; */
      margin-right: auto;
      margin-left: 0.75rem;
      img {
        transition: width 0.5s ease;
      }
    }
    .app-selector {
      position: absolute;
      left: 3.7rem;
      bottom: 0.75rem;
      white-space: nowrap;
    }
  }
  .search-wrapper {
    margin-bottom: 1rem;
    display: flex;
    input {
      width: 15rem;
    }
  }
  .app-pages-nav {
    flex: 1;
    overflow: auto;
    margin-bottom: 1rem;
  }
  .nav-group {
    display: flex;
    flex-direction: column;
  }
  .nav-section-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.55rem 1rem 0.15rem;
    margin-top: 0.35rem;
    color: ${css.navText};
    opacity: 0.5;
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.09em;
    text-transform: uppercase;
    white-space: nowrap;
    overflow: hidden;
    cursor: pointer;
    user-select: none;
    &:hover {
      opacity: 0.75;
    }
    .caret {
      flex: none;
      transition: transform 0.2s ease;
      svg {
        width: 1rem;
        * {
          stroke: ${css.navText};
          fill: ${css.navText};
        }
      }
    }
  }
  .nav-group.section-collapsed .nav-section-title .caret {
    transform: rotate(-90deg);
  }
  .nav-item.child {
    height: 2.9rem;
    .icon {
      margin-left: 2rem;
    }
    svg {
      width: 1.25rem;
    }
    .text {
      font-size: 1rem;
      font-weight: 400;
    }
  }
  &.collapsed {
    .nav-section-title {
      justify-content: center;
      padding-left: 0;
      padding-right: 0;
      gap: 0;
      cursor: default;
      span, .caret {
        display: none;
      }
    }
    .nav-item.child .icon {
      margin-left: 1rem;
    }
  }
  .global-pages-nav {
    margin-top: auto;
    hr {
      border: none;
      border-top: 1px solid ${css.navBorder};
    }
  }
  &.print-mode {
    display: none;
  }
  .mobile-toggle {
    display: none;
  }
  ${css.mobileMedia} {
    position: fixed;
    z-index: 4;
    resize: none;
    width: ${css.mobileNavWidth};
    /* The desktop max-width: 16rem was clamping this to 256px while left stayed at -18rem, so the
       drawer's right edge sat at -32px — and the toggle, anchored to that edge, rendered half off the
       left of the screen. Both must resolve to the same width. */
    max-width: ${css.mobileNavWidth};
    left: -${css.mobileNavWidth};
    transition: left 0.2s ease;
    overflow: visible;
    height: 100%;
    box-sizing: border-box;
    &.open {
      left: 0;
    }
    .mobile-toggle {
      display: block;
      position: absolute;
      top: 1rem;
      right: -3rem;
      &.open {
        right: 1rem;
        z-index: 2;
      }
      .icon {
        &.hide {
          display: none;
        }
      }
      svg {
        width: 2rem;
        * {
          stroke: ${css.black};
          fill: ${css.black};
        }
      }
      &.open svg * {
        stroke: ${css.navText};
        fill: ${css.navText};
      }
    }
    .mobile-overlay {
      position: fixed;
      top: 0;
      right: 0;
      height: 100%;
      width: 100%;
      background: #000;
      opacity: 0;
      pointer-events: none;
      transition: width 0.2s ease, opacity 0.5s ease;
      &.open {
        z-index: 3;
        opacity: 0.3;
        pointer-events: initial;
        width: calc(100% - ${css.mobileNavWidth});
      }
    }
  }
`;

export interface NavItemConfig {
  path: string,
  text?: string,
  icon?: IconKey,
  children?: React.ReactNode,
}

/** A top-level nav entry: either a section header with nested `items`, or (with `path`, no items) a standalone link. */
interface NavGroup {
  id: string,
  label: string,
  icon?: IconKey,
  path?: string,
  items?: NavItemProps[],
}

interface NavProps {
  account?: Account;
  items?: (NavItemConfig | null)[],
  excludeFooterItems?: ('orgs' | 'users' | 'orgUsers')[],
}

/**
 * The side-nav sections in display order, filtered to what this user/account may open. The post-login
 * landing logic reads the same list, so "first page in the Nav" means exactly what the Nav shows.
 */
export const navGroupsFor = (user?: Meteor.User, account?: Account): NavGroup[] => {
  const su = user?.isSuperUser;
  const hasRole = (role: string) => !!su || !!account?.roles?.includes(role);

  const dashboardItems: NavItemProps[] = [];
  if (hasRole(Roles.ricExecutive)) dashboardItems.push({ id: 'ricDashboard', path: '/ric-dashboard/ytd', text: strings.Ric(), icon: 'queryStats' });

  const developmentItems: NavItemProps[] = [];
  if (hasRole(Roles.prospector)) developmentItems.push({ id: 'prospectorDev', path: '/prospector', text: strings.Prospector(), icon: 'imageSearch' });
  if (hasRole(Roles.generate) || hasRole(Roles.luminia)) developmentItems.push({ id: 'projects', path: '/projects', text: strings.Generate(), icon: 'solarPower' });
  if (hasRole(Roles.resigen)) developmentItems.push({ id: 'resigen', path: '/resigen', text: strings.ResiGen(), icon: 'homeWork' }); // dealer prepayment module

  // Inventory is its own grant — the projectManagement role alone must not expose the org equipment pool.
  const inventoryItems: NavItemProps[] = [];
  if (hasRole(Roles.inventory)) {
    inventoryItems.push({ id: 'inventory', path: '/inventory', text: strings.Dashboard(), icon: 'shelves' });
    inventoryItems.push({ id: 'inventoryPallets', path: '/inventory/pallet-inventory', text: strings.InventoryPalletInventory(), icon: 'category' });
    inventoryItems.push({ id: 'inventorySites', path: '/inventory/sites', text: strings.InventorySites(), icon: 'homeWork' });
    inventoryItems.push({ id: 'inventoryDeliveries', path: '/inventory/deliveries', text: strings.InventoryDeliveries(), icon: 'send' });
    inventoryItems.push({ id: 'inventoryLots', path: '/inventory/lots', text: strings.InventoryLots(), icon: 'requestPage' });
    inventoryItems.push({ id: 'inventoryShipments', path: '/inventory/shipments', text: strings.InventoryShipments(), icon: 'publish' });
    inventoryItems.push({ id: 'inventorySerials', path: '/inventory/serials', text: strings.InventorySerials(), icon: 'documentSearch' });
    inventoryItems.push({ id: 'inventoryWarehouses', path: '/inventory/warehouses', text: strings.InventoryWarehouses(), icon: 'dns' });
    if (su) inventoryItems.push({ id: 'inventoryImport', path: '/inventory/import', text: strings.InventoryImport(), icon: 'upload' });
  }

  // Documents is its own grant too: the drive holds legal, finance and HR folders, so reaching a project
  // says nothing about reaching it. Paired with admin the same role administers permissions.
  const documentItems: NavItemProps[] = [];
  if (hasRole(Roles.documents)) documentItems.push({ id: 'documents', path: '/documents', text: strings.AllDocuments(), icon: 'folder' });

  const dataHubItems: NavItemProps[] = [];
  if (hasRole(Roles.productionMeters)) dataHubItems.push({ id: 'productionMeters', path: '/production-meters', text: strings.ProductionData(), icon: 'developerBoard' });
  if (hasRole(Roles.consumptionMeters)) dataHubItems.push({ id: 'consumptionMeters', path: '/consumption-meters', text: strings.ConsumptionData(), icon: 'electricMeter' });
  if (hasRole(Roles.utilityAccounts)) dataHubItems.push({ id: 'utilityAccounts', path: '/utility-accounts', text: strings.UtilityAccounts(), icon: 'electricalServices' });
  if (su || (account?.roles?.includes(Roles.consumptionMeters) && account?.roles?.includes(Roles.productionMeters))) dataHubItems.push({ id: 'energyExport', path: '/energy-export', text: strings.DataExport(), icon: 'batteryShare' });

  const projectManagementItems: NavItemProps[] = [];
  if (hasRole(Roles.projectManagement)) projectManagementItems.push({ id: 'pmProjects', path: '/project-management', text: strings.Projects(), icon: 'filePresent' });
  // Portfolios (the org grouping behind project.portfolioId) is super-user territory only.
  if (su) projectManagementItems.push({ id: 'pmPortfolios', path: '/project-management/portfolios', text: strings.Portfolios(), icon: 'category' });
  // Team (org stakeholder directory + bulk manager) is admin-only — same rule as projectManagementIsAdmin.
  if (hasRole(Roles.admin) && hasRole(Roles.projectManagement)) projectManagementItems.push({ id: 'pmTeam', path: '/project-management/team', text: strings.Team(), icon: 'group' });
  // Invoices (the org's unpaid payables queue) is for finance viewers — super users, or accounting + PM.
  if (projectManagementCanSeeFinances(user, account)) projectManagementItems.push({ id: 'pmInvoices', path: '/project-management/invoices', text: strings.Invoices(), icon: 'requestPage' });
  if (su) projectManagementItems.push({ id: 'pmSettings', path: '/project-management/settings', text: strings.Settings(), icon: 'settings' });

  const billingItems: NavItemProps[] = [];
  if (su) billingItems.push({ id: 'solarBills', path: '/solar-bills', text: strings.SolarBills(), icon: 'requestPage' });
  if (hasRole(Roles.calpine)) billingItems.push({ id: 'calpine', path: '/calpine/list', text: strings.CeaBills(), icon: 'requestPage' });

  const systemItems: NavItemProps[] = [
    { id: 'messages', path: '/messages', children: <MessagesNavItem />, icon: 'chat' },
    { id: 'support', path: '/support', text: strings.Support(), icon: 'help' },
    // Super users manage org-wide config here; everyone else sees only their own account, so 'Settings' reads truer for them.
    { id: 'manage', path: '/manage', text: su ? strings.Manage() : strings.Settings(), icon: 'manageAccounts' },
  ];

  return [
    { id: 'dashboard', label: strings.Dashboard(), icon: 'queryStats', items: dashboardItems },
    { id: 'development', label: strings.ProjectDevelopment(), icon: 'solarPower', items: developmentItems },
    { id: 'projectManagement', label: strings.ProjectManagement(), icon: 'filePresent', items: projectManagementItems }, // PM module (prototype)
    { id: 'inventoryManagement', label: strings.InventoryManagement(), icon: 'shelves', items: inventoryItems },
    { id: 'documentManagement', label: strings.DocumentManagement(), icon: 'folder', items: documentItems },
    { id: 'dataHub', label: strings.DataHub(), icon: 'batteryShare', items: dataHubItems },
    { id: 'billing', label: strings.Billing(), icon: 'requestPage', items: billingItems },
    { id: 'system', label: strings.System(), icon: 'manageAccounts', items: systemItems },
  ];
};

/** Paths of every page the Nav offers this user, in Nav order. */
export const navPathsFor = (user?: Meteor.User, account?: Account) => {
  return navGroupsFor(user, account).flatMap((group) => group.items ? group.items.map((item) => item.path) : group.path ? [group.path] : []);
};

/**
 * Where a login (or a bare `/`) lands: the main-Nav page the user last opened if they may still open it,
 * else Manage for super users, else the first page in the Nav.
 */
export const landingPathFor = (user?: Meteor.User, account?: Account) => {
  const paths = navPathsFor(user, account);
  const remembered = user?.settings?.landingPath;
  if (typeof remembered === 'string' && paths.includes(remembered)) return remembered;
  if (user?.isSuperUser) return '/manage';
  return paths[0] || '/manage';
};

/** Remembers a main-Nav page as where this user's next login should land. */
export const rememberLandingPath = (user: Meteor.User | undefined, path: string) => {
  if (!user || user.settings?.landingPath === path) return;
  setSetting.call({ key: 'landingPath', value: path });
};

/** Collapsible side navigation listing app pages filtered by user role. */
export const Nav = (props: NavProps) => {
  const account = props.account;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [selectedPath, setSelectedPath] = useState(window.location.pathname);
  const [collapsed, setCollapsed] = useState(false);
  // Which accordion sections are collapsed, persisted across reloads (device-local, like the nav-collapse).
  const [collapsedSections, setCollapsedSections] = useState<{ [id: string]: boolean }>(() => {
    try {
      return JSON.parse(localStorage.getItem('nav.collapsedSections') || '{}');
    }
    catch (_) {
      return {};
    }
  });
  const toggleSection = (id: string) => setCollapsedSections((prev) => {
    const next = { ...prev, [id]: !prev[id] };
    try {
      localStorage.setItem('nav.collapsedSections', JSON.stringify(next));
    }
    catch (_) { /* private mode */ }
    return next;
  });
  const user = useUser();
  const groups = navGroupsFor(user, account);
  const select = (path: string) => {
    setSelectedPath(path);
    rememberLandingPath(user, path);
  };
  return (
    <NavStyled id="sidenav" className={`nav${mobileOpen ? ' open' : ''}${collapsed ? ' collapsed' : ''}`}
      onClick={() => { defer(() => mobileOpen && setMobileOpen(false)); }}
    >
      <div className={`logo-wrapper${collapsed ? ' collapsed' : ''}`}>
        <Logo size={3} onDark />
        <Icon className={collapsed ? 'collapsed' : ''} onClick={() => setCollapsed(!collapsed)} icon="keyboardDoubleArrowLeft" />
      </div>
      <div className="app-pages-nav">
        {
          (props.items || []).map((item) => {
            if (!item) return null;
            return item.children ?
              <NavItem id={item.path} key={item.path} path={item.path} selected={selectedPath === item.path} onClick={() => select(item.path)}>{item.children}</NavItem>
              :
              <NavItem id={item.path} key={item.path} icon={item.icon} text={item.text} path={item.path} selected={selectedPath === item.path} onClick={() => select(item.path)} />;
          })
        }
        {
          groups.map((group) => {
            if (group.items) {
              if (!group.items.length) return null;
              const sectionCollapsed = !!collapsedSections[group.id];
              return (
                <div className={`nav-group${sectionCollapsed ? ' section-collapsed' : ''}`} key={group.id}>
                  <div className="nav-section-title" role="button" tabIndex={0} onClick={() => toggleSection(group.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSection(group.id); } }}
                  >
                    <span>{group.label}</span>
                    <Icon className="caret" icon="expandMore" />
                  </div>
                  {
                    // The whole-nav icon rail has no headers to expand from, so it always shows the children.
                    (collapsed || !sectionCollapsed) && group.items.map((child) => (
                      child.children ?
                        <NavItem className="child" id={child.path} key={child.path} path={child.path} selected={selectedPath === child.path} onClick={() => select(child.path)}>{child.children}</NavItem>
                        :
                        <NavItem className="child" id={child.path} key={child.path} icon={child.icon} text={child.text} path={child.path} selected={selectedPath === child.path} onClick={() => select(child.path)} />
                    ))
                  }
                </div>
              );
            }
            return (
              <NavItem id={group.path!} key={group.id} icon={group.icon} text={group.label} path={group.path!} selected={selectedPath === group.path} onClick={() => select(group.path!)} />
            );
          })
        }
      </div>
      <div className={`mobile-overlay${mobileOpen ? ' open' : ''}`} onClick={() => setMobileOpen(false)}></div>
      <div className={`mobile-toggle${mobileOpen ? ' open' : ''}`} onClick={() => setMobileOpen(!mobileOpen)}>
        <Icon className={mobileOpen ? 'close' : ' hide close'} icon="close" />
        <Icon className={!mobileOpen ? 'menu' : ' hide menu'} icon="menu" />
      </div>
    </NavStyled>
  );
};

const NavItemStyled = styled(Link)`
  height: 3.5rem;
  display: flex;
  border-radius: 3.5rem;
  padding-right: 1rem;
  position: relative;
  text-decoration: none;
  .icon {
    margin-top: auto;
    margin-bottom: auto;
    margin-left: 1rem;
  }
  svg {
    width: 1.5rem;
    * {
      stroke: ${css.navText};
      fill: ${css.navText};
    }
  }
  &:hover {
    background: ${css.navSurface};
    svg {
      width: 1.5rem;
    }
  }
  &.selected {
    background: ${css.navSurface};
  }
  .text {
    font-size: 1.1rem;
    font-weight: 500;
    text-transform: capitalize;
    color: ${css.navText};
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    margin-left: 1rem;
    max-width: 100%;
    overflow-x: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    transition: color 0.2s ease;
  }
  .badge {
    position: absolute;
    top: 0.25rem;
    left: 0.25rem;
    background: ${css.red2};
    color: ${css.white};
    height: 1rem;
    border-radius: 0.5rem;
    padding: 0 0.3rem;
    font-size: 0.8rem;
    ${css.flexVerticalCenter};
  }
`;

interface NavItemProps {
  id: string;
  icon?: IconKey;
  iconProps?: Partial<IconProps>;
  text?: string;
  textStyle?: React.CSSProperties;
  path: string;
  className?: string;
  children?: React.ReactNode;
  selected?: boolean;
  onClick?: () => void;
}

/** Single navigation link row with optional icon, text, or custom children. */
export const NavItem = (props: NavItemProps) => {
  const { id, icon, text, textStyle, path, className, children, selected, onClick } = props;
  return (
    <NavItemStyled id={id} className={`nav-item${selected ? ' selected' : ''}${className ? ` ${className}` : ''}`} to={path} onClick={() => onClick?.()}>
      {icon ? <Icon icon={icon} /> : null}
      {
        children ?
          children
          :
          <>
            {text && <span className="text" style={textStyle}>{text}</span> || null}
          </>
      }

    </NavItemStyled>
  );
};
